import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '@/lib/axios';
import { PostCard } from './PostCard';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { SocialPost } from '../types';

function feedSessionStorageKey(communityId: string) {
  return `feedSession:${communityId}`;
}

interface FeedProps {
  communityId: string;
  fetchEndpoint?: string;
  emptyTitle?: string;
  emptySubtitle?: string;
  unreadPostIds?: string[];
  onUnreadViewed?: (postId: string) => void;
  onSessionIdChange?: (sessionId: string | null) => void;
  refreshToken?: number;
  onRefreshed?: () => void;
  unreadFocusToken?: number;
}

export function Feed({
  communityId,
  fetchEndpoint,
  emptyTitle = 'No posts yet',
  emptySubtitle = 'Be the first to share something with the community!',
  unreadPostIds = [],
  onUnreadViewed,
  onSessionIdChange,
  refreshToken = 0,
  onRefreshed,
  unreadFocusToken = 0,
}: FeedProps) {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(feedSessionStorageKey(communityId));
    } catch {
      return null;
    }
  });
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocusingUnread, setIsFocusingUnread] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [scrollToPostId, setScrollToPostId] = useState<string | null>(null);
  const [entrancePostId, setEntrancePostId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const postElRefs = useRef(new Map<string, HTMLDivElement>());
  const isLoadingRef = useRef(false);
  const lastRefreshTokenRef = useRef(0);
  const lastUnreadFocusTokenRef = useRef(0);
  const unreadIdSet = useMemo(() => new Set(unreadPostIds), [unreadPostIds]);
  const unreadPostIdsRef = useRef(unreadPostIds);
  unreadPostIdsRef.current = unreadPostIds;
  const postsRef = useRef(posts);
  postsRef.current = posts;

  const [searchParams] = useSearchParams();
  const pinnedPostId = searchParams.get('postid');

  const endpoint = fetchEndpoint || `/api/communities/${communityId}/posts/feed`;
  const isRankedFeed = !fetchEndpoint;

  const persistSessionId = useCallback(
    (id: string | null) => {
      setSessionId(id);
      onSessionIdChange?.(id);
      try {
        const key = feedSessionStorageKey(communityId);
        if (id) sessionStorage.setItem(key, id);
        else sessionStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
    [communityId, onSessionIdChange]
  );

  const fetchPosts = useCallback(
    async (offset: number, activeSessionId: string | null) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ offset: String(offset) });
        if (isRankedFeed && activeSessionId) {
          params.set('sessionId', activeSessionId);
        }
        const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
        const data = response.data;

        if (typeof data.sessionId === 'string') {
          persistSessionId(data.sessionId);
        }

        setPosts((prev) => {
          if (offset === 0) return data.posts;
          const seen = new Set(prev.map((p) => p.id));
          const appended = (data.posts as SocialPost[]).filter((p) => !seen.has(p.id));
          return [...prev, ...appended];
        });
        setNextOffset(data.nextOffset);
        setHasMore(data.hasMore);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
        setError('Failed to load posts. Please try again.');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [endpoint, isRankedFeed, persistSessionId]
  );

  const refreshFeed = useCallback(
    async (pinnedPostId?: string | null) => {
      if (!isRankedFeed) {
        setPosts([]);
        setNextOffset(null);
        setHasMore(true);
        await fetchPosts(0, null);
        onRefreshed?.();
        return;
      }

      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const refreshParams = pinnedPostId ? `?postid=${encodeURIComponent(pinnedPostId)}` : '';
        const response = await axiosInstance.post(
          `/api/communities/${communityId}/posts/feed/refresh${refreshParams}`
        );
        const data = response.data;
        if (typeof data.sessionId === 'string') {
          persistSessionId(data.sessionId);
        }
        setPosts(data.posts ?? []);
        setNextOffset(data.nextOffset);
        setHasMore(!!data.hasMore);
        onRefreshed?.();
      } catch (err) {
        console.error('Failed to refresh feed:', err);
        setError('Failed to refresh posts. Please try again.');
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [communityId, fetchPosts, isRankedFeed, onRefreshed, persistSessionId]
  );

  const focusUnread = useCallback(async () => {
    const ids = unreadPostIdsRef.current;
    if (ids.length === 0) return;

    const loaded = postsRef.current;
    const alreadyVisibleId = ids.find((id) => loaded.some((p) => p.id === id));
    if (alreadyVisibleId) {
      setScrollToPostId(alreadyVisibleId);
      return;
    }

    setIsFocusingUnread(true);
    try {
      const response = await axiosInstance.get(`/api/communities/${communityId}/posts/unread`);
      const hydrated = (response.data.posts as SocialPost[] | undefined) ?? [];
      const target =
        ids.map((id) => hydrated.find((p) => p.id === id)).find(Boolean) ?? hydrated[0];

      if (!target) return;

      setPosts((prev) => {
        if (prev.some((p) => p.id === target.id)) return prev;
        return [target, ...prev];
      });
      setEntrancePostId(target.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Failed to load unread post:', err);
    } finally {
      setIsFocusingUnread(false);
    }
  }, [communityId]);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(feedSessionStorageKey(communityId));
    } catch {
      stored = null;
    }
    setSessionId(stored);
    onSessionIdChange?.(stored);
    setPosts([]);
    setNextOffset(null);
    setHasMore(true);
    if (pinnedPostId && isRankedFeed) {
      void refreshFeed(pinnedPostId);
      return;
    }
    void fetchPosts(0, stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, endpoint]);

  useEffect(() => {
    if (!refreshToken || refreshToken === lastRefreshTokenRef.current) return;
    lastRefreshTokenRef.current = refreshToken;
    void refreshFeed();
  }, [refreshToken, refreshFeed]);

  useEffect(() => {
    if (!unreadFocusToken || unreadFocusToken === lastUnreadFocusTokenRef.current) return;
    lastUnreadFocusTokenRef.current = unreadFocusToken;
    void focusUnread();
  }, [unreadFocusToken, focusUnread]);

  useEffect(() => {
    if (!scrollToPostId) return;
    const el = postElRefs.current.get(scrollToPostId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollToPostId(null);
  }, [scrollToPostId, posts]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && nextOffset !== null) {
          void fetchPosts(nextOffset, sessionId);
        }
      },
      { threshold: 0.1 }
    );

    if (triggerRef.current) {
      observerRef.current.observe(triggerRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [hasMore, isLoading, nextOffset, fetchPosts, sessionId, posts.length]);

  const handlePostDelete = (id: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
  };

  if (error && posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center space-y-4">
        <AlertCircle className="h-10 w-10 text-destructive opacity-50" />
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={() => void fetchPosts(0, sessionId)}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AnimatePresence initial={false}>
        {isFocusingUnread && (
          <motion.div
            key="unread-focus-loader"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm font-medium text-primary">
              <Loader2 className="h-4 w-4 animate-spin" />
              Bringing your new post…
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {posts.map((post) => {
          const isUnread = unreadIdSet.has(post.id);
          const isEntrance = entrancePostId === post.id;
          return (
            <motion.div
              key={post.id}
              ref={(node) => {
                if (node) postElRefs.current.set(post.id, node);
                else postElRefs.current.delete(post.id);
              }}
              layout={Boolean(entrancePostId)}
              initial={
                isEntrance ? { opacity: 0, y: -36, scale: 0.92, filter: 'blur(4px)' } : false
              }
              animate={{
                opacity: 1,
                y: 0,
                scale: 1,
                filter: 'blur(0px)',
              }}
              transition={
                isEntrance
                  ? { type: 'spring', stiffness: 320, damping: 24, mass: 0.85 }
                  : { layout: { type: 'spring', stiffness: 380, damping: 32 } }
              }
              onAnimationComplete={() => {
                if (isEntrance) {
                  window.setTimeout(() => setEntrancePostId(null), 900);
                }
              }}
              className={cn(
                'rounded-2xl transition-[box-shadow,ring] duration-700',
                isEntrance && 'ring-2 ring-primary/35 shadow-lg shadow-primary/20'
              )}
            >
              <PostCard
                post={post}
                onDelete={handlePostDelete}
                communityId={communityId}
                isUnread={isUnread}
                onUnreadViewed={onUnreadViewed}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>

      {isLoading && !isFocusingUnread && (
        <div className="flex justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {!hasMore && posts.length > 0 && (
        <div className="text-center p-8 text-muted-foreground text-sm">
          You've reached the end of the list.
        </div>
      )}

      {!hasMore && posts.length === 0 && !isLoading && !error && (
        <div className="text-center p-12 text-muted-foreground">
          <p className="text-lg font-medium">{emptyTitle}</p>
          <p className="text-sm">{emptySubtitle}</p>
        </div>
      )}

      {hasMore && <div ref={triggerRef} className="h-4 w-full" />}
    </div>
  );
}
